import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { OrderService } from '../../services/order.service';
import { AuthService } from '../../services/auth.service';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-address-dialog',
  templateUrl: './address-dialog.component.html',
  styles: [`
    @media (max-width: 639px) {
      :host ::ng-deep .address-modal {
        width: 95vw !important;
        max-height: 85vh !important;
      }
      :host ::ng-deep .address-modal .modal-body {
        padding: 1rem !important;
      }
    }
  `]
})
export class AddressDialogComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() editData: any = null;
  @Input() inline = false;
  @Output() saved = new EventEmitter<any>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('addrOverlay') addrOverlay!: ElementRef;
  @ViewChild('addrWrapper') addrWrapper!: ElementRef;
  private movedToBody = false;

  form: any = this.emptyForm();
  loading = false;
  error = '';

  // City autocomplete
  citySearch$ = new Subject<string>();
  cities: any[] = [];
  cityLoading = false;
  showCityDropdown = false;
  selectedCity: any = null;
  citySearchText = '';

  constructor(private orderService: OrderService, private auth: AuthService) {
    // City search with debounce
    this.citySearch$.pipe(
      debounceTime(300),
      switchMap(query => {
        if (!query || query.length < 2) {
          this.cities = [];
          this.cityLoading = false;
          return [];
        }
        this.cityLoading = true;
        return this.orderService.searchCities(query).pipe(
          finalize(() => this.cityLoading = false)
        );
      })
    ).subscribe((res: any) => {
      this.cities = res?.data || res || [];
      this.showCityDropdown = this.cities.length > 0;
    });
  }

  private emptyForm(): any {
    return { id: 0, fullname: '', mobile: '', email: '', address: '', zipcode: '', city: '', city_id: 0, company_name: '', gst: '', pan: '' };
  }

  ngOnChanges(changes: SimpleChanges) {
    // Repopulate the form when the dialog opens OR when editData changes
    // while the dialog is already open (clicking Edit on a different address
    // while another edit form is showing).
    const opening = changes['isOpen'] && this.isOpen;
    const editDataChanged = changes['editData'] && this.isOpen;
    if (opening || editDataChanged) {
      if (this.editData?.id) {
        this.form = {
          id: this.editData.id,
          fullname: this.editData.fullname || this.editData.name || '',
          mobile: this.editData.mobile || '',
          email: this.editData.email || '',
          address: this.editData.address || '',
          zipcode: this.editData.zipcode || '',
          city: this.editData.city || '',
          city_id: this.editData.city_id || (this.editData.city?.id) || 0,
          company_name: this.editData.company_name || '',
          gst: this.editData.gst || '',
          pan: this.editData.pan || '',
        };
        // Set city display
        if (this.editData.city && typeof this.editData.city === 'object') {
          this.selectedCity = this.editData.city;
          this.citySearchText = this.displayCity(this.editData.city);
        } else {
          this.citySearchText = this.editData.city || '';
          this.selectedCity = null;
        }
      } else {
        this.form = this.emptyForm();
        this.citySearchText = '';
        this.selectedCity = null;
      }
      this.error = '';
      this.cities = [];
      this.showCityDropdown = false;
      if (opening && !this.inline) {
        document.body.style.overflow = 'hidden';
        setTimeout(() => this.moveToBody(), 0);
      }
    } else if (changes['isOpen'] && !this.isOpen) {
      if (!this.inline) {
        document.body.style.overflow = '';
        this.removeFromBody();
      }
    }
  }

  ngOnDestroy() {
    if (!this.inline) {
      document.body.style.overflow = '';
      this.removeFromBody();
    }
  }

  private moveToBody() {
    if (this.movedToBody) return;
    const o = this.addrOverlay?.nativeElement;
    const w = this.addrWrapper?.nativeElement;
    if (o) document.body.appendChild(o);
    if (w) document.body.appendChild(w);
    this.movedToBody = true;
  }

  private removeFromBody() {
    if (!this.movedToBody) return;
    const o = this.addrOverlay?.nativeElement;
    const w = this.addrWrapper?.nativeElement;
    if (o?.parentNode === document.body) document.body.removeChild(o);
    if (w?.parentNode === document.body) document.body.removeChild(w);
    this.movedToBody = false;
  }

  get isEditMode(): boolean {
    return !!this.form.id && this.form.id > 0;
  }

  // City autocomplete
  onCityInput(value: string) {
    this.citySearchText = value;
    this.selectedCity = null;
    this.form.city_id = 0;
    this.citySearch$.next(value);
  }

  selectCity(city: any) {
    this.selectedCity = city;
    this.form.city_id = city.id;
    this.form.city = city;
    this.citySearchText = this.displayCity(city);
    this.showCityDropdown = false;
    this.cities = [];
  }

  displayCity(city: any): string {
    if (!city || !city.name) return '';
    let display = city.name;
    if (city.state?.name) display += ` (${city.state.name}`;
    if (city.state?.country?.name) display += `, ${city.state.country.name})`;
    else if (city.state?.name) display += ')';
    return display;
  }

  hideCityDropdown() {
    setTimeout(() => this.showCityDropdown = false, 200);
  }

  get isCityValid(): boolean {
    return !!this.selectedCity && this.selectedCity.id > 0;
  }

  close() {
    if (!this.inline) {
      document.body.style.overflow = '';
      this.removeFromBody();
    }
    this.closed.emit();
  }

  save() {
    const f = this.form;
    if (!f.fullname || !f.mobile || !f.address || !f.zipcode) {
      this.error = 'Please fill all required fields';
      return;
    }
    if (String(f.mobile).length < 10) { this.error = 'Mobile must be 10 digits'; return; }
    if (String(f.zipcode).length < 6) { this.error = 'Zipcode must be 6 digits'; return; }

    // City validation — must be selected from dropdown
    if (!this.isCityValid) {
      this.error = 'Please select a valid city from the dropdown';
      return;
    }

    this.loading = true;
    this.error = '';

    // Auto-fill email from logged-in user if missing
    if (!f.email && this.auth.currentUser?.email) {
      f.email = this.auth.currentUser.email;
    }
    if (this.auth.currentUser?.id) {
      f.user_id = this.auth.currentUser.id;
    }

    // Build clean payload — backend sets customer_id & email from user_id
    const payload: any = {
      fullname: f.fullname,
      mobile: f.mobile,
      address: f.address,
      zipcode: f.zipcode,
      city_id: this.selectedCity.id,
      company_name: f.company_name || '',
      gst: f.gst || '',
      pan: f.pan || '',
      user_id: this.auth.currentUser?.id || 0,
    };
    // Only send id for updates
    if (f.id && f.id > 0) {
      payload.id = f.id;
    }

    const apiCall = payload.id > 0
      ? this.orderService.updateAddress(payload)
      : this.orderService.addAddress(payload);

    apiCall.subscribe({
      next: (res: any) => {
        const savedAddr = res?.data || res;
        this.loading = false;
        this.close();
        this.saved.emit({ address: savedAddr, isEdit: !!(f.id && f.id > 0) });
      },
      error: (err: any) => {
        this.error = err?.error?.message || 'Failed to save address. Please try again.';
        this.loading = false;
      }
    });
  }
}
